import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { fromByteArray } from 'base64-js';
import { Buffer } from 'buffer';
import { convert } from 'html-to-text';
import { jwtDecode } from 'jwt-decode';
import { catchError, EMPTY, forkJoin, from, Observable, of, switchMap } from 'rxjs';
import { concatMap, map, tap, toArray } from 'rxjs/operators';
import { IdentityStorage } from 'storage-service-lib';
import * as project from '../../../project.json';
import { DEFAULT_BULLHORN_PROMPT_FIELDS } from '../constants/default-bullhorn-prompt-fields';
import { IBullhornEntity } from '../types/bullhorn/bullhorn-entity';
import { BullhornEntityType } from '../types/bullhorn/bullhorn-entity-type';
import { IBullhornPostResponse, IBullhornResult } from '../types/bullhorn/bullhorn-result';
import { IJobSubmission } from '../types/bullhorn/job-submission';
import { INote } from '../types/bullhorn/note';
import { IEntitySourceData } from '../types/IEntitySourceData';
import { DateFilterType, IDateFilter, IFilter, SortFilterType } from '../types/prompt-profile';
import { AppBridgeService, IAppBridge } from './app-bridge.service';
import { IBullhornEntityMeta, IBullhornFieldMeta, IEntityLabelPair } from './bullhorn-meta.service';

@Injectable({
  providedIn: 'root',
})
export class BullhornDataService {
  private appBridge: IAppBridge;
  private entities: IEntityLabelPair[] = [];
  private META_KEY = 'bullhornMeta';
  private fullEntityMeta!: IBullhornEntityMeta;
  private readonly NOTE_ACTION_FIELD = 'action';
  private readonly DEFAULT_NOTE_ACTION_LABEL = 'Note Action';
  private static readonly SOFT_DELETABLE_ENTITIES = new Set([
    'Candidate',
    'ClientContact',
    'JobOrder',
    'Opportunity',
    'Lead',
  ]);

  constructor(
    private appBridgeService: AppBridgeService,
    private http: HttpClient,
    private identityStorage: IdentityStorage,
  ) {
    this.appBridge = appBridgeService.create(project.name);
    console.debug(`Initialised AppBridge for ${project.name}`);
  }

  public getDefaultPromptFieldsForEntity(bullhornEntityType: BullhornEntityType): string[] {
    return [...(DEFAULT_BULLHORN_PROMPT_FIELDS.get(bullhornEntityType) ?? [])];
  }

  getEntityByIdV7(entity: BullhornEntityType, entityId: number, fields?: any): IEntitySourceData {
    if (!fields) {
      fields = this.updateSourceData(this.getDefaultPromptFieldsForEntity(entity) as string[]);
    }

    return {
      id: entityId.toString(),
      sourcePathCommand: {
        namespace: 'bullhorn',
        path: [entity, entityId.toString()],
      },
      fields,
    };
  }

  // TODO - will be removed but need to sort out the other entities on the card first
  getEntityById(
    entity: BullhornEntityType,
    entityId: number,
    fields?: string[],
  ): Observable<IBullhornEntity> {
    if (!fields) {
      fields = this.getDefaultPromptFieldsForEntity(entity);
    }

    return from(this.appBridge.httpGET(`entity/${entity}/${entityId}?fields=${fields}`)).pipe(
      tap((result) => {
        if (!result) {
          throw new Error('No data returned from Bullhorn API');
        }
        if (result.error?.errorMessage) {
          throw new Error(result.error.errorMessage);
        }
      }),
      catchError((error) => {
        throw Error(error);
      }),
      map((bullhornResult: IBullhornResult) => {
        return bullhornResult.data.data;
      }),
      map((entity: IBullhornEntity | any) => {
        if (entity['description']) {
          entity.description = convert(entity.description);
        }
        if (entity['notes']) {
          entity.notes = entity.notes.data.map((n: any) => {
            return convert(n.comments);
          });
        }
        console.log(entity);
        return entity;
      }),
    );
  }

  getActiveEntities(entityType: string, ids: string[], fields: string[]): Observable<any[]> {
    return entityType === 'Candidate'
      ? this.search(entityType, this.buildActiveQuery(entityType, ids), fields)
      : this.query(entityType, this.buildActiveWhere(entityType, ids), fields);
  }

  private buildActiveQuery(entityType: string, ids: string[]): string {
    const clauses = [`id:(${ids.join(' ')})`];
    clauses.push('NOT status:Archive');
    if (this.canEntityBeSoftDeleted(entityType)) {
      clauses.push('isDeleted:0');
    }
    return clauses.join(' AND ');
  }

  private buildActiveWhere(entityType: string, ids: string[]): string {
    const clauses = [`id IN (${ids.join(', ')})`];
    clauses.push("status <> 'Archive'");
    if (this.canEntityBeSoftDeleted(entityType)) {
      clauses.push('isDeleted = false');
    }
    return clauses.join(' AND ');
  }

  private canEntityBeSoftDeleted(entityType: string): boolean {
    return BullhornDataService.SOFT_DELETABLE_ENTITIES.has(entityType);
  }

  search(entityType: BullhornEntityType | string, query: string, fields?: string[]) {
    return from(
      this.appBridge.httpGET(
        `search/${entityType}?query=${query}&fields=${fields?.join(',')}`,
        120_000,
      ),
    ).pipe(
      tap((result) => {
        if (result.error?.errorMessage) {
          throw new Error(result.error.errorMessage);
        }
        if (result.errorMessage) {
          throw new Error(result.errorMessage);
        }
      }),
      catchError((error) => {
        throw Error(error);
      }),
      map((bullhornResult) => {
        return bullhornResult.data.data;
      }),
    );
  }

  /**
   *
   * @param entityType e.g. BullhornEntity.JobOrder or "JobSubmission"
   * @param where e.g. "candidate.id=123"
   * @param fields e.g. ['id', 'status']
   * @param count
   * @param sort e.g. "-id", "-dateAdded"
   */
  query(
    entityType: BullhornEntityType | string,
    where: string,
    fields?: string[],
    count?: number,
    sort?: string,
  ) {
    return from(
      this.appBridge.httpGET(
        `query/${entityType}?where=${where}&fields=${fields?.join(',') ?? '*'}&orderBy=${
          sort ?? '-id'
        }&count=${count ?? 500}`,
        120_000,
      ),
    ).pipe(
      tap((result) => {
        if (result.error?.errorMessage) {
          throw new Error(result.error.errorMessage);
        }
        if (result.errorMessage) {
          throw new Error(result.errorMessage);
        }
      }),
      catchError((error) => {
        throw Error(error);
      }),
      map((bullhornResult: IBullhornResult) => {
        return bullhornResult.data.data;
      }),
    );
  }

  updateEntity(
    entityName: string,
    entityId: number,
    postData: Object,
  ): Observable<IBullhornPostResponse> {
    return from(
      this.appBridge.httpPOST(`entity/${entityName}/${entityId}`, JSON.stringify(postData)),
    ).pipe(
      tap((result) => {
        if (result.error?.errorMessage) {
          throw new Error(result.error.errorMessage);
        }
      }),
      map((result) => {
        return result.data;
      }),
      catchError((error) => {
        throw Error(error);
      }),
    );
  }

  getNoteActionTypes(): Observable<{ label: string; types: string[] }> {
    return this.getEntityMeta(BullhornEntityType.NOTE, [this.NOTE_ACTION_FIELD]).pipe(
      switchMap((meta: any) => this.resolveNoteActionTypes(meta)),
      catchError(() => of({ label: this.DEFAULT_NOTE_ACTION_LABEL, types: [] })),
    );
  }

  getNoteActionsForIds(noteIds: string[]): Observable<{ id: number; action: string }[]> {
    if (noteIds.length === 0) {
      return of([]);
    }
    const query = `noteID:(${noteIds.join(' ')}) AND isDeleted:false`;
    return this.search(BullhornEntityType.NOTE, query, ['id', this.NOTE_ACTION_FIELD]).pipe(
      catchError(() => of([])),
    );
  }

  private resolveNoteActionTypes(meta: any): Observable<{ label: string; types: string[] }> {
    const actionField = meta.fields?.find((f: any) => f.name === this.NOTE_ACTION_FIELD);
    const label: string = actionField?.label ?? this.DEFAULT_NOTE_ACTION_LABEL;
    const optionsType = actionField?.optionsType;

    if (!optionsType) {
      return of({ label, types: this.extractInlineNoteActionTypes(actionField) });
    }

    return this.fetchRemoteNoteActionTypes(optionsType).pipe(map((types) => ({ label, types })));
  }

  private extractInlineNoteActionTypes(actionField: any): string[] {
    return (actionField?.options ?? [])
      .map((o: any) => o.label ?? o.value)
      .filter((label: string) => !this.isSeparatorOption(label));
  }

  private fetchRemoteNoteActionTypes(optionsType: string): Observable<string[]> {
    return this.http
      .get<any>(this.getrestUrl() + `/options/${optionsType}`)
      .pipe(
        map((response: any) =>
          (response.data ?? [])
            .map((item: any) => item.label ?? item.value ?? item)
            .filter((label: string) => !this.isSeparatorOption(label)),
        ),
      );
  }

  private isSeparatorOption(label: string): boolean {
    return !label || /^[-\s]+$/.test(label);
  }

  createNote(putData: string, noteId?: string) {
    if (noteId) {
      return from(this.appBridge.httpPOST(`entity/Note/${noteId}`, putData));
    } else {
      return from(this.appBridge.httpPUT(`entity/Note`, putData));
    }
  }

  createTxtFile(content: string, personId: number, fileName: String) {
    const base64Content = fromByteArray(new TextEncoder().encode(content));
    const putData = {
      externalID: 'portfolio',
      fileContent: base64Content,
      fileType: 'SAMPLE',
      name: fileName,
      contentType: 'text/plain',
    };

    return from(this.appBridge.httpPUT(`file/Candidate/${personId}`, putData));
    // Send the PUT request
  }

  getJobSubmissions(where: string) {
    const fields = [
      'id',
      'status',
      'jobOrder(clientCorporation(name))',
      'candidate(firstName,description)',
    ];

    return this.query(
      BullhornEntityType.JOB_SUBMISSION,
      `${where} AND status<>'Archive' AND isDeleted=false`,
      fields,
      200,
      '-dateAdded',
    ).pipe(
      tap((result) => {
        if (result.error?.errorMessage) {
          throw new Error(result.error.errorMessage);
        }
        if (result.errorMessage) {
          throw new Error(result.errorMessage);
        }
      }),
      catchError((error) => {
        throw Error(error);
      }),
      map((jobSubmissions: IJobSubmission[]) => {
        jobSubmissions.forEach((jobSubmission) => {
          const candidate = jobSubmission.candidate;
          candidate.displayIdentifier = `${candidate.firstName ?? '[No First Name]'} ${
            candidate.lastName ?? '[No Last Name]'
          }`;

          const jobOrder = jobSubmission.jobOrder;
          jobOrder.displayIdentifier = `${jobOrder.title} at ${jobOrder.clientCorporation?.name}`;

          if (where.startsWith('jobOrder')) {
            jobSubmission.displayIdentifier = `${candidate.displayIdentifier}`;
          } else {
            jobSubmission.displayIdentifier = `${jobOrder.displayIdentifier}`;
          }
        });

        return jobSubmissions.sort((a, b) => {
          const canA = a.candidate.firstName ?? '' + a.candidate.lastName;
          const canB = b.candidate.firstName ?? '' + b.candidate.lastName;

          if (canA < canB) {
            return -1;
          }
          if (canA > canB) {
            return 1;
          }

          return 0;
        });
      }),
    );
  }

  getFileById(path: string[]): Observable<string> {
    const url = `file/${path[0]}/${path[1]}/${path[2]}`;

    return from(this.appBridge.httpGET(url)).pipe(
      map((response: any) => {
        // Decode and return file content
        const data = response.data.File.fileContent;
        const decodedContent = Buffer.from(data, 'base64').toString('utf-8');
        return decodedContent ? decodedContent : '';
      }),
    );
  }

  modifyFile(path: string[], fileContent: string): Observable<void> {
    const url = `file/${path[0]}/${path[1]}/${path[2]}`;

    return from(this.appBridge.httpGET(url)).pipe(
      switchMap((response: any) => {
        const file = response.data.File;
        file.fileContent = Buffer.from(fileContent, 'utf-8').toString('base64');
        return from(this.appBridge.httpPOST(url, file));
      }),
    );
  }

  // TODO : to eventually be replaced by the listFilteredFiles when noteTaker gets filterable features
  listFiles(listParams: any): Observable<any[]> {
    const url = `entity/${listParams.entity.type}/${listParams.entity.id}/${listParams.fields}?fields=`;
    const selectFields = ['id', 'name', 'fileSize', 'dateAdded', 'type', 'contentSubType'];
    const whereClause = 'isDeleted = false';

    const buildObs = (firstPage: any) => {
      const totalToPull = firstPage.data.data.fileAttachments.total;
      const pageSize = 200;
      const pageCount = Math.ceil(totalToPull / pageSize);
      const obs = [];
      for (let i = 1; i <= pageCount; i++) {
        const start = (i - 1) * pageSize;
        const parameters = `${selectFields.toString()}&where=${whereClause}&start=${start}&count=${pageSize}`;
        const result$ = from(this.appBridge.httpGET(url + parameters));
        obs.push(result$);
      }
      return obs;
    };

    const firstPage$ = from(
      this.appBridge.httpGET(
        `entity/${listParams.entity.type}/${listParams.entity.id}?fields=fileAttachments`,
      ),
    );
    return firstPage$.pipe(
      map((first) => buildObs(first)),
      concatMap((obs) =>
        forkJoin(obs).pipe(
          concatMap((obs) => obs.map((fa: any) => fa.data)),
          toArray(),
        ),
      ),
      tap((res) =>
        console.debug(
          `${res.length} ${listParams.fields} pulled for ${listParams.entity.type} ${listParams.entity.id}.`,
        ),
      ),
    );
  }

  listFilteredFiles(
    listParams: any,
    fileFilter: IFilter | undefined,
    allowedFileExtensions: string[],
  ): Observable<any[]> {
    const url = `entity/${listParams.entity.type}/${listParams.entity.id}/${listParams.fields}?fields=`;
    const selectFields = ['id', 'name', 'fileSize', 'dateAdded', 'type', 'contentSubType'];
    const totalToPull = fileFilter?.count ?? 15;

    let remainingToPull = totalToPull;
    let pageSize = 200;
    const pageCount = Math.ceil(totalToPull / pageSize);
    const obs: Observable<any>[] = [];

    for (let i = 1; i <= pageCount; i++) {
      const start = (i - 1) * pageSize;
      if (pageSize > remainingToPull) {
        pageSize = remainingToPull;
      }
      const parameters = `${selectFields.toString()}&start=${start}&count=${pageSize}&where=${this.buildFFWhereClause(
        fileFilter,
        allowedFileExtensions,
      )}`;
      const result$ = from(this.appBridge.httpGET(url + parameters));
      obs.push(result$);
      remainingToPull = remainingToPull - pageSize;
    }

    return forkJoin(obs).pipe(
      concatMap((results) => results.map((result: any) => result.data)),
      toArray(),
      tap((res) =>
        console.debug(
          `${res.length} ${listParams.fields} pulled for ${listParams.entity.type} ${listParams.entity.id}.`,
        ),
      ),
    );
  }

  // Todo : consider a function which can handle building the clause for noteFilter and fileFilter
  private buildFFWhereClause(
    fileFilter: IFilter | undefined,
    allowedFileExtensions: string[],
  ): string {
    const dateClause = this.buildFFDateQuery(fileFilter?.date);
    const typeClause = this.buildFFTypeQuery(fileFilter?.category);
    const notDeletedClause = 'isDeleted = false';

    // Build file extension clause that checks both filename AND contentSubType
    // This ensures files without extensions in their name are still included if they have the correct MIME type
    const fileExtensionClauses = allowedFileExtensions.map((ext) => {
      const contentTypes = this.getContentTypesForExtension(ext);
      const nameLikeClause = `name LIKE '%25.${ext}'`;
      const contentTypeClauses = contentTypes.map((ct) => `contentSubType='${ct}'`).join(' OR ');
      return contentTypeClauses ? `(${nameLikeClause} OR ${contentTypeClauses})` : nameLikeClause;
    });
    const fileExtensionClause = fileExtensionClauses.join(' OR ');

    const clauses = [dateClause, typeClause, fileExtensionClause, notDeletedClause].filter(Boolean);
    return '(' + clauses.join(') AND (') + ')'; // Join with 'AND'
  }

  /**
   * Maps file extensions to their corresponding MIME types (contentSubType values)
   * This allows filtering by content type rather than just filename extension
   */
  private getContentTypesForExtension(extension: string): string[] {
    const extensionMap: Record<string, string[]> = {
      doc: ['application/msword', 'application/vnd.ms-word', 'msword', 'doc'],
      docx: [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'vnd.openxmlformats-officedocument.wordprocessingml.document',
        'docx',
      ],
      pdf: ['application/pdf', 'pdf'],
      txt: ['text/plain', 'plain', 'text', 'txt'],
    };
    return extensionMap[extension.toLowerCase()] || [];
  }

  private buildFFDateQuery(date: IDateFilter | undefined): string {
    // Defaults to previous 14 days
    let days = 14;
    if (date && date.rangeType === DateFilterType.LATEST && date.timeAmount) {
      days = date.timeAmount;
    }
    if (days !== -1) {
      const millisecondsAllowed = days * 86400000;
      const unixMilliseconds: number = Date.now();
      return `dateAdded >= ${unixMilliseconds - millisecondsAllowed}`;
    } else {
      return '';
    }
  }

  private buildFFTypeQuery(type: string[] | undefined): string {
    if (type && type.length !== 0) {
      let typeClause = '';
      type.forEach((item) => {
        typeClause += `type = '${item}' OR `;
      });
      return typeClause.slice(0, -4);
    } else {
      return '';
    }
  }

  filteredListNotes(listParams: any, noteFilter: IFilter | undefined): Observable<INote[]> {
    const fields = 'id,action,dateLastModified';
    // If noteFilter?.sort doesn't exist, this makes the default in order of descending.
    const sort = noteFilter?.sort ?? SortFilterType.NEWEST;
    const count = noteFilter?.count ?? 15;

    let query = this.getEntityQuery(listParams);
    if (query === '') {
      console.error(`Entity type ${listParams.entity.type} not handled.`);
      return EMPTY;
    }
    query = this.buildNFActionQuery(noteFilter?.category, query);
    query = this.buildNFDateQuery(noteFilter?.date, query);
    query += ` AND isDeleted:false`;

    // Map the sort field to use dateLastModified instead of dateAdded for notes
    const sortField = sort === SortFilterType.NEWEST ? '-dateLastModified' : 'dateLastModified';

    const url = `search/Note?query=${query}&fields=${fields}&count=${count}&sort=${sortField}`;
    const firstPage$ = from(this.appBridge.httpGET(url));
    return firstPage$.pipe(
      map((response: { data: any; error?: string }) => {
        return response.data.data;
      }),
    );
  }

  private getEntityQuery(listParams: any): string {
    const entityIdMap: Partial<Record<BullhornEntityType, string>> = {
      [BullhornEntityType.CANDIDATE]: 'candidateUserID',
      [BullhornEntityType.CLIENT_CONTACT]: 'clientContactUserID',
      [BullhornEntityType.CLIENT_CORPORATION]: 'clientCorporationID',
      [BullhornEntityType.JOB_ORDER]: 'jobOrderID',
      [BullhornEntityType.LEAD]: 'leadUserID',
      [BullhornEntityType.OPPORTUNITY]: 'opportunityID',
      [BullhornEntityType.PLACEMENT]: 'placementID',
    };
    // TODO: special case for JOB_SUBMISSION additional entites may need implemented

    return (
      entityIdMap[listParams.entity.type as keyof typeof entityIdMap]?.concat(
        `:${listParams.entity.id}`,
      ) || ''
    );
  }

  private buildNFActionQuery(action: string[] | undefined, query: string): string {
    if (!action || action.length === 0) {
      return query;
    }
    let actionQuery = '';
    action.forEach((item) => {
      actionQuery += `action:("${item}") OR `;
    });
    return `${query} AND (${actionQuery.slice(0, -4)})`;
  }

  private buildNFDateQuery(date: IDateFilter | undefined, query: string): string {
    if (!date) {
      // Defaults to previous 14 days
      const startDate = this.getDateInBoston(14);
      return `${query} AND dateLastModified:[${startDate} TO *]`;
    } else if (date.rangeType === DateFilterType.LATEST && date.timeAmount) {
      if (date.timeAmount !== -1) {
        const startDate = this.getDateInBoston(date.timeAmount);
        return `${query} AND dateLastModified:[${startDate} TO *]`;
      } else {
        return query;
      }
    }
    return query;
  }

  private getDateInBoston(days: number): string {
    const options: Intl.DateTimeFormatOptions = {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };
    const currentDateBoston = new Date().toLocaleString('en-US', options);
    const [month, day, year] = currentDateBoston.split('/');
    const currentDate = new Date(`${year}-${month}-${day}`);
    currentDate.setDate(currentDate.getDate() - days);
    return currentDate.toISOString().slice(0, 10).replace(/-/g, '');
  }

  getFields(entity: string, id: number, fields: string[]): Observable<IBullhornResult> {
    return from(
      this.appBridge.httpGET(`entity/${entity}/${id}?fields=${fields?.join(',') ?? '*'}`),
    ).pipe(
      map((response: { data: IBullhornResult; error?: string }) => {
        return response.data;
      }),
    );
  }

  getAssociation(entity: string, id: number, association: string, count = 500): Observable<any[]> {
    return from(
      this.appBridge.httpGET(`entity/${entity}/${id}/${association}?fields=id&count=${count}`),
    ).pipe(map((response: any) => response.data?.data ?? []));
  }

  insertInto(model: any, fieldPath: string): any {
    const path: string[] = fieldPath.split('.');
    model = this.initialiseModel(model, path);
    const state = { parent: null, entry: model };
    let pathIndex = 0;
    while (pathIndex < path.length) {
      if (typeof state.entry === 'string') {
        this.handleStringEntry(path, pathIndex, state);
      } else if (Array.isArray(state.entry)) {
        this.handleArrayEntry(path, pathIndex, state);
      } else {
        this.handleObjectMap(path, pathIndex, state);
      }

      pathIndex++;
    }
    return model;
  }

  public toBullhornEntity(label: string) {
    switch (label) {
      case 'Job Posting':
        return BullhornEntityType.JOB_ORDER;
      case 'Client Contact':
        return BullhornEntityType.CLIENT_CONTACT;
      case 'Client':
        return BullhornEntityType.CLIENT_CORPORATION;
      default:
        const entity = this.entities.find((pair) => pair.label === label);
        return entity ? (entity.objectName as BullhornEntityType) : (label as BullhornEntityType);
    }
  }

  public async getEntityMetaFullFromStorage(
    entityType: BullhornEntityType,
    fields?: string[],
  ): Promise<IBullhornEntityMeta> {
    if (!this.fullEntityMeta) {
      await this.initializeEntityMeta(entityType);
    }

    if (fields) {
      const filteredFields = this.fullEntityMeta.fields.filter((field) =>
        fields.includes(field.name),
      );
      const copyMeta = structuredClone(this.fullEntityMeta);
      copyMeta.fields = filteredFields;
      return copyMeta;
    } else {
      return this.fullEntityMeta;
    }
  }

  public async truncateField(
    entityType: BullhornEntityType,
    fieldName: string,
    value: any,
  ): Promise<string> {
    if (!this.fullEntityMeta) {
      await this.initializeEntityMeta(entityType);
    }

    const foundField = this.fullEntityMeta.fields.find(
      (field: IBullhornFieldMeta) => field.name === fieldName,
    );

    if (foundField) {
      if (foundField.dataType === 'String') {
        if (foundField.maxLength && value.length > foundField.maxLength) {
          console.log(
            `${entityType} ${foundField.label} #${
              foundField.name
            }: Substituting ${value} with ${value.substring(0, foundField.maxLength)}`,
          );
          value = value.substring(0, foundField.maxLength);
        }
      }
    }
    return value;
  }

  // TODO - move all meta calls to BullhornMeta service
  private initializeEntityMeta(entityType: BullhornEntityType): Promise<void> {
    return new Promise<void>((resolve) => {
      const META_KEY_FULL = this.META_KEY + (entityType as string);

      if (!this.getLocalEntityMeta(META_KEY_FULL)) {
        this.getEntityMeta(entityType).subscribe({
          next: (response) => {
            localStorage.setItem(META_KEY_FULL, JSON.stringify(response));
            this.fullEntityMeta = response;
            console.log('Full entity meta retrieved from bullhorn and stored locally.');
            resolve();
          },
        });
      } else {
        this.fullEntityMeta = JSON.parse(localStorage.getItem(META_KEY_FULL) as string);
        console.log('Entities retrieved from local storage.');
        resolve();
      }
    });
  }

  getEntityMeta(
    entityType: BullhornEntityType | string,
    fields?: string[],
    meta?: 'full' | 'basic',
  ): Observable<IBullhornEntityMeta> {
    return from(
      this.http
        .get<any>(
          this.getrestUrl() +
            `/meta/${entityType}?fields=${fields?.join(',') ?? '*'}&meta=${meta ?? 'full'}`,
        )
        .pipe(
          map((response) => {
            return response as IBullhornEntityMeta;
          }),
        ),
    );
  }

  public settings(settings: string) {
    return this.http.get<any>(this.getrestUrl() + `/settings/${settings}`);
  }

  public getLeadsAndOpportuniteisEnabled(): Observable<boolean> {
    return from(
      this.http.get<any>(this.getrestUrl() + `/settings/leadAndOpportunityEnabled`).pipe(
        map((response) => {
          return response.leadAndOpportunityEnabled;
        }),
      ),
    );
  }

  private getrestUrl(): string {
    const storedJson = this.identityStorage.get()!;
    const jwt = jwtDecode<any>(storedJson.token);
    return jwt.restUrl;
  }

  private getLocalEntityMeta(key: string) {
    return JSON.parse(localStorage.getItem(key) as string);
  }

  private initialiseModel(model: any, path: string[]): any {
    model = model ?? {};
    if (path.length > 0) {
      const root = path[0];
      if (!model[root]) {
        model[root] = {};
      }
    }
    return model;
  }

  updateSourceData(fields: string[]) {
    let sourceFields = {};
    if (Array.isArray(fields)) {
      fields.forEach((field) => {
        sourceFields = this.insertInto(sourceFields, 'bullhorn.' + field);
      });
    } else {
      sourceFields = fields;
    }
    return sourceFields;
  }

  private handleStringEntry(
    path: string[],
    pathIndex: number,
    state: { entry: any; parent: any },
  ): void {
    const ei = state.parent!.findIndex((f: any) => f === state.entry);
    state.parent!.splice(ei, 1); // remove the string entry in the parent / owner object
    const obj: any = [path[pathIndex]];
    state.parent!.push({ [state.entry]: obj }); // replace with an array (e.g. "documents": ["AttachmentName"])
    state.parent = obj;
    state.entry = obj[0];
  }

  private handleArrayEntry(
    path: string[],
    pathIndex: number,
    state: { entry: any; parent: any },
  ): void {
    // the array can contain plain strings or complete objects
    let item = state.entry.find((k: any) => {
      if (typeof k === 'string') {
        return k === path[pathIndex];
      }
      return Object.keys(k).includes(path[pathIndex]);
    });

    if (!item) {
      if (pathIndex < path.length - 1) {
        const obj: any = [];
        state.entry.push({ [path[pathIndex]]: obj });
        state.entry = obj;
      } else {
        state.entry.push(path[pathIndex]);
      }
    } else {
      // if the item is an object then we want to use the value of that object pointed to by our path
      if (typeof item === 'object') {
        item = Object.entries(item as object).find(([k, v]) => k === path[pathIndex])![1];
      }
      state.parent = state.entry;
      state.entry = item;
    }
  }

  private handleObjectMap(
    path: string[],
    pathIndex: number,
    state: { entry: any; parent: any },
  ): void {
    const entries = Object.entries(state.entry);

    let entry: any = entries.find(([k, v]) => k === path[pathIndex]);
    entry = entry ? entry[1] : undefined;

    if (!entry) {
      if (pathIndex < path.length - 1) {
        const obj: any = [];
        state.entry[path[pathIndex]] = obj;
        state.entry = obj;
      } else {
        state.entry.push(path[pathIndex]);
      }
    } else {
      state.entry = entry;
    }
  }
}
